import React, { useState, useEffect, useContext } from "react";

import Select from "react-select";
import { Trans, useTranslation } from "react-i18next";

import UserContext from "../../lib/UserContext";
import supportFormSchema from "../../schemas/supportForm.schema";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import EmailAPI from "../../API/email.api";
import LogCapture from "../../utils/LogManagement/LogHelper";
import ValidationError from "../../components/Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import { getReactSelectTheme } from "../../utils/ReactSelectStyles";

const platformOptions = [
    { label: "NeoSpectra Portal", value: "NeoSpectra Portal" },
    { label: "NeoSpectra Scan Application", value: "NeoSpectra Scan" },
    { label: "NeoSpectra Collect Application", value: "NeoSpectra Collect" },
];

const issueOptions = [
    { label: "Ask Question", value: "Ask Question" },
    { label: "Request Enhancement", value: "Request Enhancement" },
    { label: "Report Issue", value: "Report Issue" },
    // { label: "Feature", value: "Feature" },
];

const opeartingSystemOptions = [
    { label: "Android", value: "Android" },
    { label: "iOS", value: "iOS" },
];

const ContactSupport = () => {
    const [formInput, setFormInput] = useState({});
    const [error, setError] = useState({});
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    const [showOS, setShowOS] = useState(false);

    const context = useContext(UserContext);
    const { t } = useTranslation();

    useEffect(() => {
        setFormInput({ email: context.getUser().email });
        // Include the Crisp code here, without the <script></script> tags
        window.$crisp = [];
        window.CRISP_WEBSITE_ID = "cb1110eb-8c06-4e3b-b3d6-2e40272c41e8";

        (function () {
            var d = document;
            var s = d.createElement("script");

            s.src = "https://client.crisp.chat/l.js";
            s.async = 1;
            d.getElementsByTagName("head")[0].appendChild(s);
            $crisp.push(["safe", true]);
        })();
    }, []);

    useEffect(() => {
        validate(
            formInput,
            supportFormSchema.ContactSupport,
            setIsFormValid,
            setError
        );
    }, [formInput]);

    const handleInputChange = (e) => {
        setFormInput({ ...formInput, [e.target.name]: e.target.value });
    };

    const handleSelectChange = (value, action) => {
        if (
            action.name === "platform" &&
            (value.value === "NeoSpectra Scan" ||
                value.value === "NeoSpectra Collect")
        ) {
            setShowOS(true);
        } else if (action.name === "platform") {
            setShowOS(false);
        }
        setFormInput({ ...formInput, [action.name]: value.value });
    };

    const handleSubmit = async () => {
        try {
            setIsFormSubmitted(true);
            setIsSubmitting(true);

            if (!isFormValid) return;

            const result = await EmailAPI.contactSupport(formInput);
            if (result) {
                SuccessAlert(t("contactSupportSuccess"));
                LogCapture.info(
                    `${formInput?.email} has sent a new request for the support of type ${formInput?.issueType}`
                );
            }
        } catch (responseError) {
            ErrorAlert(responseError?.message || responseError);
            LogCapture.error(
                `${
                    formInput?.email
                } couldn't send a new request for the support of type ${
                    formInput?.issueType
                }, error: ${responseError?.message || responseError}`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <header className="main-content-header flex items-center justify-between">
                <h1 className="h1 capitalize">
                    <Trans i18nKey="contactUs">Contact Us</Trans>
                </h1>
            </header>
            <div className="card-table shadow">
                <div className="upload-img-box flex">
                    <div className="w-full">
                        <h3 className="capitalize text-center color-blue">
                            <i className="fas fa-phone-alt mr-2" />
                            <Trans i18nKey="callUsAt">
                                you can give us a call at
                            </Trans>
                            <span className="medium-title-bold ">
                                (844) 985-4800{" "}
                            </span>
                        </h3>
                        <div className="my-14 flex">
                            {/* OR section */}
                            <div className="word-inside-line"></div>
                        </div>
                        <h3 className="medium-title-bold capitalize">
                            <Trans i18nKey="contantInformation">
                                contact information
                            </Trans>
                        </h3>
                        <hr className="mt-2 mb-5" />
                        <div
                            className={`tags input-group ${
                                error.email && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="email">E-Mail</Trans>
                            </h4>
                            <input
                                type="email"
                                name="email"
                                value={formInput.email || ""}
                                onChange={handleInputChange}
                                className="input-element"
                                placeholder="user@domain.com"
                            />
                            {isFormSubmitted && (
                                <ValidationError errorMessage={error.email} />
                            )}
                        </div>
                        <div
                            className={`tags input-group ${
                                error.phoneNumber && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs">
                                <Trans i18nKey="phoneNumber">
                                    phone number
                                </Trans>
                            </h4>
                            <input
                                type="text"
                                name="phoneNumber"
                                value={formInput.phoneNumber || ""}
                                onChange={handleInputChange}
                                className="input-element"
                                placeholder="Ex: (844) 985-4800"
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.phoneNumber}
                                />
                            )}
                        </div>
                        <h3 className="medium-title-bold capitalize pt-4">
                            <Trans i18nKey="requestDetails">
                                request details
                            </Trans>
                        </h3>
                        <hr className="mt-2 mb-5" />
                        <div
                            className={`tags input-group ${
                                error.platform && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="whichPlatform">
                                    Which platform are you using?
                                </Trans>
                            </h4>
                            <Select
                                className="basic-single"
                                isSearchable={false}
                                options={platformOptions}
                                name="platform"
                                onChange={handleSelectChange}
                                placeholder={t("selectPlaceholder", {
                                    entity: t("platform"),
                                })}
                                theme={getReactSelectTheme}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.platform}
                                />
                            )}
                        </div>

                        {showOS && (
                            <div
                                className={`tags input-group ${
                                    error.operatingSystem && isFormSubmitted
                                        ? "invalid-warning-input"
                                        : ""
                                }`}
                            >
                                <h4 className="uppercase text-xs required">
                                    <Trans i18nKey="whichOperationSystem">
                                        Which Opearting System?
                                    </Trans>
                                </h4>
                                <Select
                                    className="basic-single"
                                    isSearchable={false}
                                    options={opeartingSystemOptions}
                                    name="operatingSystem"
                                    onChange={handleSelectChange}
                                    placeholder={t("selectPlaceholder", {
                                        entity: t("operatingSystem"),
                                    })}
                                    theme={getReactSelectTheme}
                                />
                                {isFormSubmitted && (
                                    <ValidationError
                                        errorMessage={error.operatingSystem}
                                    />
                                )}
                            </div>
                        )}

                        <div
                            className={`tags input-group ${
                                error.issueType && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="typeOfProblem">
                                    what is the type of your request?
                                </Trans>
                            </h4>
                            <Select
                                className="basic-single"
                                isSearchable={false}
                                options={issueOptions}
                                name="issueType"
                                onChange={handleSelectChange}
                                placeholder={t("selectPlaceholder", {
                                    entity: t("type"),
                                })}
                                theme={getReactSelectTheme}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.issueType}
                                />
                            )}
                        </div>

                        <div
                            className={`tags input-group ${
                                error.issueDescription && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="describeRequest">
                                    describe your request
                                </Trans>
                            </h4>
                            <textarea
                                className="input-element textarea-element"
                                value={formInput.issueDescription || ""}
                                name="issueDescription"
                                onChange={handleInputChange}
                                placeholder={t("supportRequestDescription")}
                            ></textarea>
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.issueDescription}
                                />
                            )}
                        </div>
                        <div className="main-customer-form-bnt flex items-center justify-end float-right mt-5">
                            <button
                                onClick={handleSubmit}
                                className={`default-orange-outline-btn ${
                                    isSubmitting ? "disabled" : ""
                                }`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="animate-spin mr-3 inline-flex">
                                            <i className="fas fa-sync"></i>
                                        </span>
                                        <Trans i18nKey="requesting">
                                            Requesting...
                                        </Trans>
                                    </>
                                ) : (
                                    <>
                                        <Trans i18nKey="submit">Submit</Trans>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContactSupport;
